import { useCallback, useMemo } from 'react'
import type { Instruction } from '@/features/instructions/types'
import { baseFromInstruction } from '@/features/instructions/lib/baseFromInstruction'
import { seedArticleForm, type ArticleForm } from '@/features/instructions/lib/articleDraft'
import { useFreshArticleModeration } from '@/features/moderation/useFreshArticleModeration'
import { useSeedReadAt } from './useSeedReadAt'

interface Args {
  slug: string | undefined
  instruction: Instruction | undefined
  /** Упала загрузка самой статьи (не `404` — у него свой экран). */
  instructionFailed: boolean
  refetchInstruction: () => void
}

/**
 * Засев формы редактора. Новая статья — сразу. Существующая — только по СВЕЖЕМУ ответу
 * `/moderation` (`useFreshArticleModeration`), и так у любой, а не только у живой: живость
 * известна лишь из этого ответа. Форма без черновика у живой статьи засеялась бы живыми
 * значениями, и дифф сохранения стёр бы черновик. `seed === undefined` — ждём.
 */
export const useArticleSeed = ({
  slug,
  instruction,
  instructionFailed,
  refetchInstruction,
}: Args): {
  seed: ArticleForm | undefined
  /** `useSeedReadAt`: не раньше какого момента ушли оба запроса засева. */
  seedReadAt: number | undefined
  loadFailed: boolean
  retry: () => void
} => {
  const moderation = useFreshArticleModeration(instruction?.id ?? -1)
  const { state, isError, refetch } = moderation

  // Засев зависит только от черновика: смена `review`/`visibility`/`hasUnsubmittedChanges`
  // не должна пересобирать форму. `undefined` — свежего ответа ещё нет.
  const draft = state === undefined ? undefined : state.draft
  const seed = useMemo(() => {
    if (!slug) return baseFromInstruction(null)
    if (!instruction || draft === undefined) return undefined
    return seedArticleForm(baseFromInstruction(instruction), draft)
  }, [slug, instruction, draft])

  const moderationFailed = instruction !== undefined && isError && state === undefined
  const retry = useCallback(() => {
    if (instructionFailed) refetchInstruction()
    if (moderationFailed) refetch()
  }, [instructionFailed, moderationFailed, refetchInstruction, refetch])

  const seedReadAt = useSeedReadAt(slug, instruction?.id)
  return { seed, seedReadAt, loadFailed: instructionFailed || moderationFailed, retry }
}
